<template>
  <div class="permission-app">
  <div class="permission-roles-view bg-slate-50">
    <permission-page-header
      icon="mdi-shield-lock-outline"
      variant="teal"
      :title="$t('permission_roles_page_title')"
      :subtitle="$t('permission_roles_page_desc')"
      :stats="headerStats"
    >
      <template #actions>
        <!-- unsaved pill（視覺與 UsersView 一致） -->
        <span
          v-if="selected && dirty"
          class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200"
          role="status"
          aria-live="polite"
        >
          <span class="w-1.5 h-1.5 rounded-full bg-amber-500 motion-safe:animate-pulse" aria-hidden="true" />
          {{ $t('permission_unsaved') }}
        </span>
        <!-- Save button（primary CTA） -->
        <button
          v-if="selected"
          type="button"
          :disabled="!dirty || saving"
          :title="$t('permission_save_changes')"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer bg-indigo-500 text-white hover:bg-indigo-600 shadow-sm hover:shadow disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed disabled:shadow-none"
          :class="{ 'ring-2 ring-indigo-300 ring-offset-1': dirty && !saving }"
          @click="save"
        >
          <v-icon size="14" color="white">
            {{ saving ? 'mdi-loading mdi-spin' : 'mdi-content-save-outline' }}
          </v-icon>
          {{ saving ? $t('permission_saving') : $t('permission_save_changes') }}
        </button>
        <!-- Divider（stats 資訊區 vs 操作區） -->
        <div v-if="selected" class="w-px h-8 bg-slate-200 mx-1" aria-hidden="true" />
        <!-- Hide legacy toggle -->
        <button
          v-if="selected"
          type="button"
          class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-colors duration-200 cursor-pointer"
          :class="hideLegacy
            ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600'
            : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'"
          :aria-pressed="hideLegacy"
          @click="hideLegacy = !hideLegacy"
        >
          <v-icon size="12" :color="hideLegacy ? 'white' : '#64748b'">mdi-eye-off-outline</v-icon>
          {{ hideLegacy ? $t('permission_hide_legacy_on') : $t('permission_hide_legacy_off') }}
        </button>
        <!-- Preview toggle -->
        <button
          v-if="selected"
          type="button"
          class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-colors duration-200 cursor-pointer"
          :class="previewMode
            ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600'
            : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'"
          :aria-pressed="previewMode"
          @click="previewMode = !previewMode"
        >
          <v-icon size="12" :color="previewMode ? 'white' : '#64748b'">mdi-eye-outline</v-icon>
          {{ previewMode ? $t('permission_preview_mode_on') : $t('permission_preview_mode_off') }}
        </button>
        <!-- Help popover（共用組件） -->
        <permission-help-popover v-if="selected" mode="role" :role-label="roleLabel" />
      </template>
    </permission-page-header>
    <permission-sub-tabs />

    <div class="p-4 grid grid-cols-12 gap-4 items-start">
      <!-- Left: role list（sticky） -->
      <aside class="col-span-12 md:col-span-4 lg:col-span-3 sticky top-4 self-start max-h-[calc(100vh-6rem)] overflow-y-auto pr-1">
        <role-list-panel
          :tab="tab"
          :list="list"
          :selected-id="selectedId"
          @change-tab="onChangeTab"
          @select="onSelect"
          @add="handleAdd"
          @delete="handleDelete"
        />
      </aside>

      <!-- Right: matrix -->
      <div class="col-span-12 md:col-span-8 lg:col-span-9 min-w-0">
        <role-matrix
          v-if="selected"
          :tab="tab"
          :selected="selected"
          :resources="resources"
          :matrix="matrix"
          :loading="loadingMatrix"
          :saving="saving"
          :dirty="dirty"
          :member-count="memberCount"
          :preview-mode="previewMode"
          :hide-legacy-resources="hideLegacy"
          @toggle-cell="onToggleCell"
          @set-row-all="onSetRowAll"
          @set-scope="onSetScope"
        />
        <div
          v-else
          class="bg-white rounded-2xl border border-slate-200 shadow-sm p-20 text-center text-slate-400"
        >
          {{ $t('permission_select_subject_left', {
            role: tab === 'department' ? $t('permission_dept_short') : $t('permission_group_short')
          }) }}
        </div>
      </div>
    </div>

    <!-- 刪除確認 dialog（R-H4 fix：取代 window.confirm）-->
    <v-dialog v-model="deleteConfirm.show" max-width="420" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="text-base font-bold">
          {{ $t('permission_delete_confirm_title', {
            label: tab === 'department' ? $t('permission_dept_short') : $t('permission_group_short')
          }) }}
        </v-card-title>
        <v-card-text class="text-sm text-slate-600">
          {{ $t('permission_delete_confirm', {
            label: tab === 'department' ? $t('permission_dept_short') : $t('permission_group_short'),
            name: deleteConfirm.item ? deleteConfirm.item.name : ''
          }) }}
        </v-card-text>
        <v-card-actions class="px-5 pb-4">
          <v-spacer />
          <v-btn text :disabled="deleteConfirm.loading" @click="deleteConfirm.show = false">
            {{ $t('common_cancel') }}
          </v-btn>
          <v-btn color="error" :loading="deleteConfirm.loading" :disabled="deleteConfirm.loading" @click="performDelete">
            {{ $t('common_confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="2500" role="alert" aria-live="polite">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
  </div>
</template>

<script>
import {
  HttpDepartment,
  HttpGroup,
  HttpResource,
  HttpPermission
} from '@/js/http-Manager'
import {
  ACTIONS,
  DEPENDENT_ACTIONS,
  supportsAction
} from '@/js/permission-helpers'
import PermissionPageHeader from '@/components/Permission/PermissionPageHeader'
import PermissionSubTabs from '@/components/Permission/PermissionSubTabs'
import PermissionHelpPopover from '@/components/Permission/PermissionHelpPopover'
import RoleListPanel from '@/components/Permission/RoleListPanel'
import RoleMatrix from '@/components/Permission/RoleMatrix'

export default {
  name: 'PermissionRolesView',
  components: { PermissionPageHeader, PermissionSubTabs, PermissionHelpPopover, RoleListPanel, RoleMatrix },
  data () {
    return {
      ACTIONS,
      tab: 'department',
      departments: [],
      groups: [],
      resources: [],
      selectedDeptId: null,
      selectedGroupId: null,
      matrix: {},
      initialMatrix: {},
      loadingMatrix: false,
      saving: false,
      previewMode: false,
      hideLegacy: (() => {
        try { const v = window.localStorage.getItem('permission_roles_hide_legacy'); return v === null ? true : v === '1' } catch (e) { return true }
      })(),
      snackbar: { show: false, text: '', color: 'success' },
      deleteConfirm: { show: false, item: null, loading: false }
    }
  },
  computed: {
    list () {
      return this.tab === 'department' ? this.departments : this.groups
    },
    selectedId () {
      return this.tab === 'department' ? this.selectedDeptId : this.selectedGroupId
    },
    selected () {
      return this.list.find(x => x.id === this.selectedId) || null
    },
    dirty () {
      return JSON.stringify(this.matrix) !== JSON.stringify(this.initialMatrix)
    },
    memberCount () {
      if (!this.selected) return '—'
      return typeof this.selected.userCount === 'number' ? this.selected.userCount : 0
    },
    roleLabel () {
      return this.tab === 'department'
        ? this.$t('permission_dept_short')
        : this.$t('permission_group_short')
    },
    headerStats () {
      return [
        { label: this.$t('permission_dept_short'), value: this.departments.length, icon: 'mdi-domain' },
        { label: this.$t('permission_group_short'), value: this.groups.length, icon: 'mdi-account-group-outline' },
        { label: this.$t('permission_resource'), value: this.resources.length, icon: 'mdi-file-document-outline' }
      ]
    }
  },
  watch: {
    hideLegacy (val) {
      try { window.localStorage.setItem('permission_roles_hide_legacy', val ? '1' : '0') } catch (e) { /* ignore quota / private mode */ }
    }
  },
  async mounted () {
    await this.loadAll()
  },
  methods: {
    onChangeTab (next) {
      this.tab = next
      const first = this.list[0]
      if (first) {
        this.onSelect(first.id)
      } else {
        this.setSelected(null)
        this.matrix = {}
        this.initialMatrix = {}
      }
    },
    onSelect (id) {
      this.setSelected(id)
      this.loadMatrix()
    },
    setSelected (id) {
      if (this.tab === 'department') this.selectedDeptId = id
      else this.selectedGroupId = id
    },
    onToggleCell ({ res, action, value }) {
      this.toggleCell(res, action, value)
    },
    onSetRowAll ({ res, enabled }) {
      const actions = res.supportedActions.split(',').map(s => s.trim())
      for (const a of actions) this.toggleCell(res, a, enabled)
    },
    onSetScope ({ res, action, scope }) {
      // 守門：未 granted 的 cell 不可寫 scope（避免送出 { isGranted:false, scope:3 } 的髒資料）
      // Matrix UI 本來就只在 cellState==='full' 時顯示 scope chip，此處再防一層
      const cell = this.matrix[res.key] && this.matrix[res.key][action]
      if (!cell || !cell.isGranted) return
      // 用 $set 確保 Vue 2 響應式觸發
      this.$set(this.matrix[res.key][action], 'scope', scope)
    },
    toggleCell (res, action, value) {
      if (!this.matrix[res.key]) this.$set(this.matrix, res.key, {})
      if (!this.matrix[res.key][action]) {
        this.$set(this.matrix[res.key], action, { isGranted: false, scope: null })
      }
      this.matrix[res.key][action].isGranted = !!value
      if (value && action !== 'view' && supportsAction(res, 'view')) {
        if (!this.matrix[res.key]['view']) {
          this.$set(this.matrix[res.key], 'view', { isGranted: false, scope: null })
        }
        this.matrix[res.key]['view'].isGranted = true
      }
      if (!value && action === 'view') {
        for (const dep of DEPENDENT_ACTIONS) {
          if (supportsAction(res, dep) && this.matrix[res.key][dep]) {
            this.matrix[res.key][dep].isGranted = false
          }
        }
      }
    },
    async loadAll () {
      try {
        const [dRes, gRes, rRes] = await Promise.all([
          HttpDepartment.GetAll(true),
          HttpGroup.GetAll(true),
          HttpResource.GetAll()
        ])
        if (dRes && dRes.status === 200) this.departments = dRes.data || []
        if (gRes && gRes.status === 200) this.groups = gRes.data || []
        if (rRes && rRes.status === 200) this.resources = rRes.data || []
        const first = this.list[0]
        if (first) this.onSelect(first.id)
      } catch (e) {
        this.showSnackbar('error', this.$t('load_fail'))
      }
    },
    async loadMatrix () {
      if (!this.selectedId) {
        this.matrix = {}
        this.initialMatrix = {}
        return
      }
      this.loadingMatrix = true
      try {
        const api = this.tab === 'department'
          ? HttpPermission.GetDepartmentMatrix(this.selectedId)
          : HttpPermission.GetGroupMatrix(this.selectedId)
        const res = await api
        const cells = (res && res.status === 200) ? res.data : []
        const map = {}
        for (const c of cells) {
          if (!map[c.resourceKey]) map[c.resourceKey] = {}
          map[c.resourceKey][c.action] = { isGranted: c.isGranted, scope: c.scope }
        }
        this.matrix = map
        this.initialMatrix = JSON.parse(JSON.stringify(map))
      } finally {
        this.loadingMatrix = false
      }
    },
    async save () {
      const cells = this.diffCells()
      if (cells.length === 0) {
        this.showSnackbar('info', this.$t('no_changes'))
        return
      }
      this.saving = true
      try {
        const api = this.tab === 'department'
          ? HttpPermission.UpsertDepartmentMatrix(this.selectedId, cells)
          : HttpPermission.UpsertGroupMatrix(this.selectedId, cells)
        const res = await api
        if (res && res.status === 200) {
          this.showSnackbar('success', this.$t('save_success'))
          await this.loadMatrix()
        } else {
          const msg = res && res.data && res.data.message
          this.showSnackbar('error', msg || this.$t('save_fail'))
        }
      } finally {
        this.saving = false
      }
    },
    diffCells () {
      const cells = []
      const keys = new Set([
        ...Object.keys(this.matrix),
        ...Object.keys(this.initialMatrix)
      ])
      for (const resourceKey of keys) {
        const curr = this.matrix[resourceKey] || {}
        const init = this.initialMatrix[resourceKey] || {}
        const actions = new Set([...Object.keys(curr), ...Object.keys(init)])
        for (const action of actions) {
          const c = curr[action]
          const i = init[action]
          const currGranted = c ? c.isGranted : false
          const currScope = c && c.isGranted ? c.scope : null
          const initGranted = i ? i.isGranted : false
          const initScope = i ? i.scope : null
          if (currGranted === initGranted && currScope === initScope) continue
          cells.push({ resourceKey, action, isGranted: currGranted, scope: currScope })
        }
      }
      return cells
    },
    async handleAdd (name) {
      try {
        const api = this.tab === 'department'
          ? HttpDepartment.Create({ name, isActive: true })
          : HttpGroup.Create({ name, isActive: true })
        const res = await api
        if (res && (res.status === 200 || res.status === 201)) {
          await this.loadAll()
          const target = this.list.find(x => x.name === name)
          if (target) this.onSelect(target.id)
        } else {
          const msg = res && res.data && res.data.message
          this.showSnackbar('error', msg || this.$t('create_fail'))
        }
      } catch (e) {
        this.showSnackbar('error', this.$t('create_fail'))
      }
    },
    handleDelete (item) {
      // R-H4 fix：改用 v-dialog（取代 window.confirm），跟 UsersView 重設 UX 一致
      this.deleteConfirm = { show: true, item, loading: false }
    },
    async performDelete () {
      const item = this.deleteConfirm.item
      if (!item) return
      this.deleteConfirm.loading = true
      try {
        const api = this.tab === 'department'
          ? HttpDepartment.Delete(item.id)
          : HttpGroup.Delete(item.id)
        const res = await api
        if (res && (res.status === 204 || res.status === 200)) {
          if (this.selectedId === item.id) this.setSelected(null)
          this.deleteConfirm.show = false
          await this.loadAll()
        } else {
          const msg = res && res.data && res.data.message
          this.showSnackbar('error', msg || this.$t('delete_fail'))
        }
      } catch (e) {
        this.showSnackbar('error', this.$t('delete_fail'))
      } finally {
        this.deleteConfirm.loading = false
      }
    },
    showSnackbar (color, text) {
      this.snackbar = { show: true, color, text }
    }
  }
}
</script>

<style scoped>
.permission-roles-view {
  margin: -20px;
  min-height: calc(100vh - 64px);
  padding-bottom: 24px;
}
</style>
